import { IFieldInfoProperties, IGetValueArgs } from "@components/smart/FieldInfo";
import { IAssetEntity } from "@odata/GeneratedEntityTypes";
import { AssetItemTypeCode } from "@odata/GeneratedEnums";
import { IFormatOptions } from "@odata/OData.utils";
import {
    getInActiveFYValidation,
    getSameOrLaterItemOfTypeValidation,
    getValidationResult
} from "@pages/asset/Asset.utils";
import { ILowerPriceCustomData } from "@pages/asset/fixedAsset/FixedAsset.utils";
import {
    _getLowerTaxAccountLocalPath,
    ILowerPriceDialogEntity,
    isApplyGrantType,
    isLowerPriceType,
    LowerPriceAmountPath,
    LowerPriceCreditAccountPath,
    LowerPriceDatePath,
    LowerPriceDebitAccountPath
} from "@pages/asset/fixedAsset/LowerTaxPrice.utils";
import { getCompanyCurrency, isNonProfitCompany } from "@utils/CompanyUtils";
import i18next from "i18next";

import { BasicInputSizes, FieldType, NavigationSource, TextAlign, ValidatorType } from "../../../enums";
import { TValue } from "../../../global.types";
import CurrencyType from "../../../types/Currency";
import { currencyScaleFormatter } from "../../../types/Number";
import { addFormDef, addFormGroup, defaultDateFromActiveFiscalYear } from "../../../views/formView/Form.utils";
import { FormStorage } from "../../../views/formView/FormStorage";
import { getSingleAccountFieldDef } from "../../accountAssignment/AccountAssignment.utils";
import { IDefinition, IGetDefinition } from "../../PageUtils";

function getLowerAccountPrefixByType(type: "Credit" | "Debit"): string {
    return type === "Debit" ? "901" : "6";
}

export function getGrantAccountFilterDefinition(type: "Credit" | "Debit"): Partial<IFieldInfoProperties> {
    return {
        filter: {
            select: (args: IGetValueArgs) => {
                const num = getLowerAccountPrefixByType(type);
                return `IsActive eq true AND startsWith(Number,'${num}')`;
            }
        },
    };
}

export const getDefinition: IGetDefinition = (context): IDefinition => {
    const isNonProfit = isNonProfitCompany(context);

    const LowerPriceAccount = getSingleAccountFieldDef({ datePath: LowerPriceDatePath });

    const _getAccountFieldDef = (type: "Credit" | "Debit"): IFieldInfoProperties => ({
        ...LowerPriceAccount,
        label: i18next.t(`FixedAsset:LowerTaxPrice.${type}`),
        isRequired: true,
        ...getGrantAccountFilterDefinition(type),
        fieldSettings: {
            ...LowerPriceAccount.fieldSettings,
            localDependentFields: LowerPriceAccount.fieldSettings.localDependentFields
                .map(ldf => ({
                    ...ldf,
                    to: { id: _getLowerTaxAccountLocalPath(type, ldf.to.id as ("Number" | "Name")) },
                    navigateFrom: NavigationSource.Parent
                }))
        },
        isReadOnly: ({ storage, bindingContext }) =>
            (storage as FormStorage<IAssetEntity, ILowerPriceCustomData>).getCustomData().DisableAccounts,
        formatter: (val: TValue, args?: IFormatOptions): string => {
            const name = args.storage.getValueByPath(_getLowerTaxAccountLocalPath(type, "Name"));
            const number = args.storage.getValueByPath(_getLowerTaxAccountLocalPath(type, "Number"));
            return val ? `${number ?? ""} - ${name ?? ""}` : "";
        },
        width: BasicInputSizes.L
    });

    const def: IDefinition = {
        form: {
            id: "LowerPriceSpecialForm",
            translationFiles: getDefinition.translationFiles,
            fieldDefinition: {
                [LowerPriceDatePath]: {
                    type: FieldType.Date,
                    label: i18next.t("FixedAsset:LowerTaxPrice.Date"),
                    isRequired: true,
                    validator: {
                        type: ValidatorType.Date,
                        settings: {
                            customValidator: (value, args) => {
                                const storage = (args.storage as FormStorage<ILowerPriceDialogEntity, ILowerPriceCustomData>).getCustomData().assetStorage;
                                const argsWithAssetStorage = { ...args, storage };
                                return getValidationResult(
                                    getInActiveFYValidation(value, args),
                                    // todo: check validations
                                    getSameOrLaterItemOfTypeValidation(value, argsWithAssetStorage, [
                                        AssetItemTypeCode.TechnicalImprovement,
                                        AssetItemTypeCode.IncreaseOfPrice,
                                        AssetItemTypeCode.ReductionOfPrice,
                                        AssetItemTypeCode.PutInUse
                                    ])
                                );
                            }
                        }
                    },
                    defaultValue: defaultDateFromActiveFiscalYear
                },
                [LowerPriceAmountPath]: {
                    type: FieldType.NumberInput,
                    label: i18next.t("FixedAsset:LowerTaxPrice.Amount"),
                    textAlign: TextAlign.Right,
                    isRequired: isLowerPriceType,
                    isReadOnly: isApplyGrantType,
                    validator: {
                        type: ValidatorType.Number,
                        settings: {
                            min: 0,
                            excludeMin: true
                        }
                    },
                    fieldSettings: {
                        formatter: currencyScaleFormatter,
                        unit: CurrencyType.getCurrencyUnit(getCompanyCurrency(context))
                    }
                }
            },
            groups: [
                {
                    id: "Common",
                    showGroupDivider: false,
                    rows: [
                        [{ id: LowerPriceDatePath }, { id: LowerPriceAmountPath }]
                    ]
                }
            ]
        }
    };

    if (isNonProfit) {
        addFormDef(def, LowerPriceDebitAccountPath, _getAccountFieldDef("Debit"));
        addFormDef(def, LowerPriceCreditAccountPath, _getAccountFieldDef("Credit"));

        addFormGroup(def, {
            id: "Accounting",
            title: i18next.t("FixedAsset:LowerTaxPrice.AccountingGroupTitle"),
            rows: [[
                { id: LowerPriceDebitAccountPath },
                { id: LowerPriceCreditAccountPath },
            ]]
        });
    }

    return def;
};

getDefinition.translationFiles = ["FixedAsset", "Common"];
